import {TIME_REG_TYPES} from '../../TimeRowUtils';
import Util from '../../../../../shared/util/util';
import CreateTimeRegistration from '../../../../../../mutations/create_time_registration_mutation.modern';
import {MODAL_TYPE, showModal} from '../../../../../shared/components/modals/generic_modal_conductor';
import UpdateTimeRegistration from '../../../../../../mutations/update_time_registration_mutation.modern';
import {dispatch, EVENT_ID} from '../../../../../../containers/event_manager';
import {createToast} from '../../../../../shared/components/toasts/another-toast/toaster';
import DeleteTimeRegistration from '../../../../../../mutations/delete_time_registration_mutation.modern';
import DeleteMultipleTimeRegistrations from '../../../../../../mutations/delete_multiple_time_regs_mutation';
import {roundToNDecimals} from '../../../../../shared/util/NumberUtil';
import {trackTimeRegistrationTargetSuggestionFeedback} from '../../../../../../tracking/tracking_types/TrackTimeRegistrationTargetSuggestion';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {canLoggedInPersonManageProgram} from '../../../../../shared/util/ProgramFinancialLogic';
import {ACTION} from '../../../../../shared/components/modals/program-management/ProgramBudgetErrorMessage';
import {APPROVAL_STATUS} from '../../../../../../constants';
import {hasFeatureFlag} from '../../../../../shared/util/FeatureUtil';
import {showUnsubmitTimeApprovalModal} from '../../../../../../containers/modal/time_approval/UnsubmitTimeApprovalModal';

// Per-status next-step for a TIME_REGISTRATION_IN_LOCKED_PERIOD server error.
// This is a defensive/residual path: canEditTimeReg already disables the edit
// affordance for locked rows up-front, so this only fires when a user bypasses
// the disabled UI (e.g. a stale cached page, or a race with another actor
// changing the row's approval status).
// SUBMITTED can be self-served (open the existing Unsubmit modal). APPROVED and
// INVOICED are intentionally informational-only: reversing an approval is an
// approver-only action elsewhere in the app, and deleting an invoice line isn't
// a timesheet-page action, so neither gets a self-service shortcut here.
function showTimeRegistrationLockedNextStep(intl, lockContext) {
	const {formatMessage} = intl;
	const approvalStatus = lockContext?.approvalStatus;

	if (approvalStatus === APPROVAL_STATUS.SUBMITTED && lockContext?.selectedPersonId && lockContext?.currentViewingDate) {
		showUnsubmitTimeApprovalModal({
			selectedPersonId: lockContext.selectedPersonId,
			selectedPersonName: lockContext.selectedPersonName,
			currentViewingDate: lockContext.currentViewingDate,
			lockedDate: lockContext.lockedDate,
			companyLockedDate: lockContext.companyLockedDate,
		});
		return;
	}

	if (approvalStatus === APPROVAL_STATUS.APPROVED) {
		createToast({
			duration: 4000,
			warning: true,
			message: formatMessage({
				id: hasFeatureFlag('timesheet_approval_new_statuses')
					? 'time_approval.locked.approved_reverse_approval'
					: 'time_approval.locked.approved_unapprove',
			}),
		});
		return;
	}

	if (approvalStatus === APPROVAL_STATUS.INVOICED) {
		createToast({
			duration: 4000,
			warning: true,
			message: formatMessage({id: 'time_approval.locked.invoiced'}),
		});
		return;
	}

	// No row-status context available (or an unrecognized status) — fall back to
	// the pre-existing generic locked-period modal rather than showing nothing.
	showModal({
		type: MODAL_TYPE.WARNING_DATE_LOCKED,
	});
}

export function handleNotesFieldBlur(
	lineItem,
	value,
	isTimeReg,
	viewer,
	date,
	selectedPerson,
	onTimeCreateSuccess,
	getIsHarvestLineItem,
	setForceUpdate,
	getIsUnit4LineItem,
	addMovedIdleTime,
	addMovedProject,
	addMovedTask,
	onTimeUpdateSuccess
) {
	if (lineItem.notes === value || (lineItem && !lineItem.notes && !value)) return; //Don't do anything if value did not change
	const val = value === '' ? null : value;
	const isSwitchIdleTime = lineItem.switchIdleTime;
	const isSwitchProject = lineItem.switchProject;
	let taskId;
	let parentTaskId;
	let projectId;
	let idleTimeId;
	let canRegister = true;
	switch (lineItem.type) {
		case TIME_REG_TYPES.TASK_TIME: {
			taskId = lineItem.id;
			parentTaskId = lineItem.parentTaskId;
			break;
		}
		case TIME_REG_TYPES.PROJECT_TIME: {
			projectId = lineItem.id;
			if (lineItem.status === 'DONE' || lineItem.status === 'HALTED') {
				canRegister = false;
			}
			break;
		}
		case TIME_REG_TYPES.IDLE_TIME: {
			idleTimeId = lineItem.id;
			break;
		}
		default: {
			// Suggestions converted to time regs
			if (isSwitchIdleTime) {
				idleTimeId = lineItem.id;
			} else if (isSwitchProject) {
				projectId = lineItem.id;
			} else if (lineItem.id) {
				taskId = lineItem.id;
				parentTaskId = lineItem.parentTaskId;
				projectId = lineItem?.project?.id;
			}
		}
	}
	if (canRegister) {
		if (!isTimeReg && val) {
			const createNoteTimeReg = (harvestTask, unit4Activity) => {
				Util.CommitMutation(
					CreateTimeRegistration,
					{
						source: 'Time-row-table',
						viewer: viewer,
						projectId: projectId,
						taskId: taskId,
						subTask: taskId ? !!parentTaskId : null,
						idleTimeId: idleTimeId,
						minutesRegistered: 0,
						notes: val,
						day: date.date(),
						month: date.month() + 1,
						year: date.year(),
						personId: selectedPerson.id,
						harvestTaskId: harvestTask ? harvestTask : null,
						unit4ActivityId: unit4Activity ? unit4Activity.value : null,
						unit4ActivityName: unit4Activity ? unit4Activity.label : null,
					},
					onTimeCreateSuccess
				);
			};
			if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem)) {
				showModal({
					value: 0,
					type: MODAL_TYPE.HARVEST_TASK_SELECT,
					timeRegName: lineItem.name,
					timeReg: lineItem,
					project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
					callback: harvestTask => createNoteTimeReg(harvestTask, null),
					defaultCallback: setForceUpdate.bind(this),
					cancelCallback: setForceUpdate.bind(this),
				});
			} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem)) {
				showModal({
					value: 0,
					type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
					timeRegName: lineItem.name,
					timeReg: lineItem,
					project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
					callback: unit4Activity => createNoteTimeReg(null, unit4Activity),
					defaultCallback: setForceUpdate.bind(this),
					cancelCallback: setForceUpdate.bind(this),
				});
			} else {
				createNoteTimeReg();
			}
			if (isSwitchIdleTime) {
				addMovedIdleTime(idleTimeId);
			} else if (isSwitchProject) {
				addMovedProject(projectId);
			} else {
				addMovedTask(taskId);
			}
		} else {
			Util.CommitMutation(
				UpdateTimeRegistration,
				{source: 'Time-row-table', id: lineItem.id, notes: val, newFormat: true},
				onTimeUpdateSuccess
			);
		}
	}
}

/**
 * The errors a time-reg mutation payload carries, if any.
 *
 * A rejection arrives as a payload error on an otherwise successful response, so it lands in the
 * success callback rather than the mutation's error path — which is why every handler below has to
 * look before it celebrates.
 */
export function timeRegPayloadErrors(result) {
	const payload = result?.createTimeReg ?? result?.updateTimeReg ?? result?.deleteMultipleTimeRegs ?? result?.deleteTimeReg;
	return payload?.errors ?? null;
}

/**
 * Whether a time-reg payload was rejected because the date is locked.
 *
 * Only one of the four payload shapes is ever present on a given response, so this reads the
 * errors flat, the way the create and update handlers always have.
 */
export function isLockedPeriodRejection(result) {
	return (timeRegPayloadErrors(result) ?? []).includes('TIME_REGISTRATION_IN_LOCKED_PERIOD');
}

/**
 * The one gate every create/update/delete success callback goes through: if the payload carries a
 * locked-period rejection, explain it with the per-status next step and report that it was handled,
 * so the caller neither claims success nor runs its success side-effects.
 *
 * Callers that have no single row in scope (bulk delete) pass no `lockContext` and get the generic
 * locked modal, which is what `showTimeRegistrationLockedNextStep` falls back to.
 */
export function handleTimeRegLockedRejection(result, intl, lockContext) {
	if (!isLockedPeriodRejection(result)) return false;
	showTimeRegistrationLockedNextStep(intl, lockContext);
	return true;
}

export function onTimeCreateSuccess(result, intl, program, actualPersonId, lockContext) {
	if (handleTimeRegLockedRejection(result, intl, lockContext)) return;

	if (result.createTimeReg.errors && result.createTimeReg.errors.length === 1) {
		if (Util.checkForSageErrorAndShowModal(result.createTimeReg.errors, false)) {
		} else if (result.createTimeReg.errors[0] === 'PROGRAM_REVENUE_LOCKED') {
			showProgramRevenueLockedMessage(actualPersonId, program);
		}
	} else {
		dispatch(EVENT_ID.TIME_REGISTRATION_MUTATION_SUCCESS, {timeRegisteredInHours: null, wasClickEvent: false});
		createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_created'})});
	}
}

function showProgramRevenueLockedMessage(actualPersonId, program) {
	const canManageProgram = canLoggedInPersonManageProgram(program?.members, actualPersonId);

	showModal({
		type: MODAL_TYPE.PROGRAM_BUDGET_ERROR_MESSAGE,
		action: ACTION.MARK_TASK_BILLABLE,
		programName: program?.name,
		canManageProgram,
		programPrefix: program?.prefix,
	});
}

export function onTimeUpdateSuccess(result, intl, overBudgetProgramsByCompanyProjectId, actualPersonId, lockContext) {
	if (handleTimeRegLockedRejection(result, intl, lockContext)) return;

	if (result.updateTimeReg.errors && result.updateTimeReg.errors.length === 1) {
		if (Util.checkForSageErrorAndShowModal(result.updateTimeReg.errors, false)) {
		} else if (result.updateTimeReg.errors[0] === 'PROGRAM_REVENUE_LOCKED') {
			const resultProject = result.updateTimeReg.timeReg.project
				? result.updateTimeReg.timeReg.project
				: result.updateTimeReg.timeReg.task.project;
			const program = overBudgetProgramsByCompanyProjectId.find(
				project => project.companyProjectId === resultProject?.companyProjectId
			)?.programInfo;
			showProgramRevenueLockedMessage(actualPersonId, program);
		}
	} else {
		createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_updated'})});
	}
}

export function onTimeDeleteSuccess(result, intl, lockContext) {
	if (handleTimeRegLockedRejection(result, intl, lockContext)) return;

	if (!Util.checkForSageErrorAndShowModal(timeRegPayloadErrors(result), false)) {
		createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_deleted'})});
	}
}

export function deleteTimeRegistrationsByIds(ids, onTimeDeleteSuccess) {
	Util.CommitMutation(DeleteMultipleTimeRegistrations, {ids}, onTimeDeleteSuccess);
}

export function deleteTimeRegistration(lineItem, viewer, onTimeDeleteSuccess) {
	Util.CommitMutation(
		DeleteTimeRegistration,
		{
			id: lineItem.timeRegId ? lineItem.timeRegId : lineItem.id,
			taskId: lineItem.task ? lineItem.task.id : undefined,
			projectId: lineItem.project ? lineItem.project.id : undefined,
			idleTimeId: lineItem.idleTime ? lineItem.idleTime.id : undefined,
			companyId: viewer.company.id,
		},
		onTimeDeleteSuccess
	);
}

export function handleWeekDayHoursInputChange(
	hours,
	selectedPerson,
	viewer,
	inputDate,
	timeForDay,
	lineItem,
	setTimeRegToFocus,
	addMovedIdleTime,
	addMovedProject,
	addMovedTask,
	getIsHarvestLineItem,
	createTimeReg,
	billableHours,
	roleId,
	notes,
	setForceUpdate,
	getIsUnit4LineItem,
	usingAdvancedTimeRegistration,
	timeRegId,
	onTimeUpdateSuccess,
	setWaitingForPreviousOperation,
	onSuccessCallback,
	onTimeDeleteSuccess
) {
	const companyProjectId = lineItem.project?.companyProjectId ? lineItem.project.companyProjectId : lineItem.companyProjectId;

	trackEvent('Lower Timesheets Time Row Hours Input', 'Changed', {
		inputHours: hours,
		timeRegPerson: selectedPerson
			? selectedPerson.id === viewer.actualPersonId
				? 'myself'
				: selectedPerson.email
			: 'myself',
		date: inputDate.format('YYYY-MM-DD'),
	});

	const hasTimeForDay = timeForDay.length > 0;
	let canRegister = true;
	let taskId;
	let parentTaskId;
	let projectId;
	let idleTimeId;
	switch (lineItem.type) {
		case TIME_REG_TYPES.TASK_TIME: {
			taskId = lineItem.id;
			parentTaskId = lineItem.parentTaskId;
			projectId = lineItem.project.id;
			if (lineItem.project.status === 'DONE' || lineItem.project.status === 'HALTED') {
				canRegister = false;
			}
			break;
		}
		case TIME_REG_TYPES.PROJECT_TIME: {
			projectId = lineItem.id;
			if (lineItem.status === 'DONE' || lineItem.status === 'HALTED') {
				canRegister = false;
			}
			break;
		}
		case TIME_REG_TYPES.IDLE_TIME: {
			idleTimeId = lineItem.id;
			break;
		}
		// TASK - Not a time reg yet
		default: {
			if (lineItem.switchIdleTime) {
				idleTimeId = lineItem.id;
				// For animations
				setTimeRegToFocus(idleTimeId);
				addMovedIdleTime(idleTimeId);
			} else if (lineItem.switchProject) {
				projectId = lineItem.id;
				// For animations
				setTimeRegToFocus(projectId);
				addMovedProject(projectId);
			} else {
				taskId = lineItem.id;
				parentTaskId = lineItem.parentTaskId;
				// For animations
				setTimeRegToFocus(taskId);
				addMovedTask(taskId);
			}
		}
	}
	if (!canRegister) {
		return;
	}
	// Don't create, always delete time reg when user types specifically 0
	if (!hasTimeForDay) {
		if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem) && hours > 0) {
			showModal({
				value: hours,
				type: MODAL_TYPE.HARVEST_TASK_SELECT,
				timeRegName: lineItem.name,
				timeReg: lineItem,
				project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
				callback: harvestTask =>
					createTimeReg(
						inputDate,
						hours,
						billableHours,
						roleId,
						notes,
						taskId,
						parentTaskId,
						projectId,
						companyProjectId,
						idleTimeId,
						selectedPerson,
						harvestTask,
						null,
						onSuccessCallback
					),
				defaultCallback: setForceUpdate.bind(this),
				cancelCallback: setForceUpdate.bind(this),
			});
		} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem) && hours > 0) {
			showModal({
				value: hours,
				type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
				timeRegName: lineItem.name,
				timeReg: lineItem,
				project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
				callback: unit4Activity =>
					createTimeReg(
						inputDate,
						hours,
						billableHours,
						roleId,
						notes,
						taskId,
						parentTaskId,
						projectId,
						companyProjectId,
						idleTimeId,
						selectedPerson,
						null,
						unit4Activity,
						onSuccessCallback
					),
				defaultCallback: setForceUpdate.bind(this),
				cancelCallback: setForceUpdate.bind(this),
			});
		} else {
			createTimeReg(
				inputDate,
				hours,
				billableHours,
				roleId,
				notes,
				taskId,
				parentTaskId,
				projectId,
				companyProjectId,
				idleTimeId,
				selectedPerson,
				null,
				null,
				onSuccessCallback
			);
		}
	} else {
		const isLocked = timeForDay.some(
			el => el.node.invoiced || el.node.economicTimeId || el.node.xeroInvoiceId || el.node.lockedInPeriod
		);
		if (isLocked) {
		} else if (hours === null && !usingAdvancedTimeRegistration) {
			// Clearing a cell is a delete, and the most common way to make one, so it goes through
			// the same handler the trash icon does — otherwise this gesture is the only one that
			// gets a bare locked modal instead of the per-status next step.
			const onSuccess = result => {
				setWaitingForPreviousOperation(false);
				onTimeDeleteSuccess(result);
			};
			trackEvent('Time For Day', 'Deleted');
			Util.CommitMutation(DeleteMultipleTimeRegistrations, {ids: timeForDay.map(tReg => tReg.node.id)}, onSuccess);
		} else {
			const anyNote = timeForDay.length > 1 && timeForDay.some(el => el.node.notes && el.node.notes.length > 0);
			if (anyNote && !usingAdvancedTimeRegistration) {
				return;
			}
			Util.CommitMutation(
				UpdateTimeRegistration,
				{
					source: 'Time-row-table',
					id: timeRegId || timeForDay[0].node.id,
					day: inputDate.date(),
					month: inputDate.clone().month() + 1,
					year: inputDate.year(),
					projectId: projectId,
					taskId: taskId,
					idleTimeId: idleTimeId,
					minutesRegistered: hours * 60,
					billableMinutesRegistered:
						billableHours === null
							? null
							: billableHours === undefined || isNaN(billableHours)
							? undefined
							: billableHours * 60,
					roleId: roleId,
					notes: typeof notes === 'string' ? notes : undefined,
					removeOthersForTheDay: !usingAdvancedTimeRegistration,
				},
				result => {
					// Only on an actual success: a locked-period rejection arrives as a payload error
					// on an otherwise successful response, and running the success side-effects on one
					// closes the popover and logs the edit as though it had been written.
					if (!isLockedPeriodRejection(result)) onSuccessCallback();
					// Week view: lineItem is the row-level entity shared across all 7 days, so its
					// .timeReg reflects whichever day was rendered last, not the day being edited here.
					// Pass timeForDay (this day's TimeRegType nodes) so the caller can resolve the
					// correct per-day approval status instead of reading the shared row entity.
					onTimeUpdateSuccess(result, lineItem, timeForDay);
				}
			);
		}
	}
}

export function handleDayHoursInputChange(
	hours,
	selectedPerson,
	viewer,
	inputDate,
	lineItem,
	addMovedIdleTime,
	addMovedProject,
	addMovedTask,
	getIsHarvestLineItem,
	createTimeReg,
	billableHours,
	roleId,
	notes,
	setForceUpdate,
	getIsUnit4LineItem,
	onTimeDeleteSuccess,
	onSuccessCallback,
	onTimeUpdateSuccess
) {
	const companyProjectId = lineItem.project?.companyProjectId ? lineItem.project.companyProjectId : lineItem.companyProjectId;

	trackEvent('Lower Timesheets Time Row Hours Input', 'Changed', {
		inputHours: hours,
		timeRegPerson: selectedPerson
			? selectedPerson.id === viewer.actualPersonId
				? 'myself'
				: selectedPerson.email
			: 'myself',
		date: inputDate.format('YYYY-MM-DD'),
	});

	const isTimeReg = lineItem.type != null;
	const isExistingTimeReg = isTimeReg && lineItem.minutesRegistered !== undefined && lineItem.id;
	const date = inputDate.clone();
	const isSwitchIdleTime = lineItem.switchIdleTime;
	const isSwitchProject = lineItem.switchProject;
	const task = isSwitchIdleTime || isSwitchProject ? null : lineItem;
	const project = isSwitchProject ? lineItem : lineItem.project;
	const idleTime = isSwitchIdleTime ? lineItem : lineItem.idleTime;
	if (isExistingTimeReg) {
		const timeReg = lineItem;
		if (hours === null) {
			// TODO, validate this is okay?
			Util.CommitMutation(
				DeleteTimeRegistration,
				{
					id: timeReg.id,
					taskId: task ? task.id : undefined,
					projectId: project ? project.id : undefined,
					idleTimeId: idleTime ? idleTime.id : undefined,
					companyId: viewer.company.id,
				},
				result => {
					if (!isLockedPeriodRejection(result)) onSuccessCallback();
					onTimeDeleteSuccess(result);
				}
			);
		} else {
			Util.CommitMutation(
				UpdateTimeRegistration,
				{
					source: 'Time-row-table',
					id: timeReg.id,
					projectId: timeReg.task ? timeReg.task.project.id : timeReg.project ? timeReg.project.id : null,
					taskId: timeReg.task ? timeReg.task.id : null,
					idleTimeId: timeReg.idleTime ? timeReg.idleTime.id : null,
					minutesRegistered: hours * 60,
					billableMinutesRegistered:
						billableHours === null
							? null
							: billableHours === undefined || isNaN(billableHours)
							? undefined
							: billableHours * 60,
					roleId: roleId,
					notes: typeof notes === 'string' ? notes : undefined,
				},
				result => {
					if (!isLockedPeriodRejection(result)) onSuccessCallback();
					// This is an update, not a delete — route it to the update-success handler
					// (which knows how to check result.updateTimeReg.errors for a locked-period
					// rejection) rather than onTimeDeleteSuccess (which only checks
					// deleteTimeReg/deleteMultipleTimeRegs and would silently miss it).
					onTimeUpdateSuccess(result, timeReg);
				}
			);
		}
	} else {
		const createTimeCallback = (harvestTask, unit4Activity) => {
			if (!isTimeReg) {
				if (isSwitchIdleTime) {
					addMovedIdleTime(idleTime.id);
				} else if (isSwitchProject) {
					addMovedProject(project.id);
				} else {
					addMovedTask(task.id);
				}
			}
			createTimeReg(
				date,
				hours,
				billableHours,
				roleId,
				notes,
				task ? task.id : null,
				task ? task.parentTaskId : null,
				project ? project.id : null,
				companyProjectId,
				idleTime ? idleTime.id : null,
				selectedPerson,
				harvestTask,
				unit4Activity,
				onSuccessCallback
			);
		};
		if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem) && hours > 0) {
			showModal({
				value: hours,
				type: MODAL_TYPE.HARVEST_TASK_SELECT,
				timeRegName: lineItem.task ? lineItem.task.name : lineItem.project ? lineItem.project.name : '',
				timeReg: lineItem,
				project: lineItem.task ? lineItem.task.project : lineItem.project,
				callback: harvestTask => createTimeCallback(harvestTask, null),
				defaultCallback: setForceUpdate.bind(this),
				cancelCallback: setForceUpdate.bind(this),
			});
		} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem) && hours > 0) {
			showModal({
				value: hours,
				type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
				timeRegName: lineItem.task ? lineItem.task.name : lineItem.project ? lineItem.project.name : '',
				timeReg: lineItem,
				project: lineItem.task ? lineItem.task.project : lineItem.project,
				callback: unit4Activity => createTimeCallback(null, unit4Activity),
				defaultCallback: setForceUpdate.bind(this),
				cancelCallback: setForceUpdate.bind(this),
			});
		} else {
			createTimeCallback(null);
		}
	}
}

export function createTimeReg(
	viewer,
	taskId,
	idleTimeId,
	projectId,
	companyProjectId,
	parentTaskId,
	hours,
	billableHours,
	roleId,
	notes,
	date,
	selectedPerson,
	harvestTask,
	unit4Activity,
	onTimeCreateSuccess,
	timeRegistrationTargetSuggestionsData,
	onSuccess
) {
	Util.CommitMutation(
		CreateTimeRegistration,
		{
			viewer: viewer,
			source: 'Time-row-table',
			projectId: projectId ? projectId : null,
			taskId: taskId ? taskId : null,
			subTask: taskId ? !!parentTaskId : null,
			idleTimeId: idleTimeId,
			minutesRegistered: roundToNDecimals(hours * 60, 0),
			billableMinutesRegistered: roundToNDecimals(billableHours * 60, 0),
			roleId: roleId,
			notes: notes,
			day: date.date(),
			month: date.month() + 1,
			year: date.year(),
			personId: selectedPerson
				? selectedPerson.actualPersonId != null
					? selectedPerson.actualPersonId
					: selectedPerson.id
				: null,
			harvestTaskId: harvestTask ? harvestTask : null,
			unit4ActivityId: unit4Activity ? unit4Activity.value : null,
			unit4ActivityName: unit4Activity ? unit4Activity.label : null,
		},
		result => {
			// Same gate the update and delete callbacks apply: a locked-period refusal comes back as
			// a payload error on a successful response, and must not run the success side-effects.
			if (!isLockedPeriodRejection(result)) onSuccess();
			onTimeCreateSuccess(result, companyProjectId);
		}
	);
	if (timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions) {
		const timeRegistrationTargetSuggestions = timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions;
		const oldTimeRegistrationTargetSuggestions =
			timeRegistrationTargetSuggestionsData?.oldTimeRegistrationTargetSuggestions;
		trackTimeRegistrationTargetSuggestionFeedback({
			companyId: viewer.company.id,
			viewerId: viewer.actualPersonId,
			personId: selectedPerson
				? selectedPerson.actualPersonId != null
					? selectedPerson.actualPersonId
					: selectedPerson.id
				: null,
			uiSource: 'timesheet-table',
			modelVersion: timeRegistrationTargetSuggestions.modelVersion,
			timeRegistrationTargetTaskId: taskId,
			timeRegistrationTargetProjectId: projectId,
			timeRegistrationTargetIdleTimeId: idleTimeId,
			timeRegistrationDate: date.format('YYYY-MM-DD'),
			timeRegistrationTargetSuggestions: timeRegistrationTargetSuggestions?.sortedSuggestions,
			oldTimeRegistrationTargetSuggestions: oldTimeRegistrationTargetSuggestions,
		});
	}
}
